import { useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X, Search, MessageCircle, Check, PackageX } from "lucide-react";
import { ProductIcon } from "@/components/ProductIcon";
import { PriceDisplay } from "@/components/PriceDisplay";
import { AvailabilityBadge } from "@/components/AvailabilityBadge";
import { formatIDR } from "@/lib/currency";
import { buildBuyWhatsAppUrl } from "@/lib/whatsapp";
import {
  getProductBySlug,
  searchProducts,
  variantIsOrderable,
  useCatalog,
} from "@/data/catalog";

export function CatalogDialog({ open, initialSlug, onSelectSlug, onClose }) {
  useCatalog();
  const [query, setQuery] = useState("");
  const [selectedSlug, setSelectedSlug] = useState(initialSlug || null);
  const [selectedCode, setSelectedCode] = useState(null);
  const variantsRef = useRef(null);

  useEffect(() => {
    if (open) setQuery("");
  }, [open]);

  // Sync selected platform from route; changing platform clears the variant.
  useEffect(() => {
    setSelectedSlug(initialSlug || null);
    setSelectedCode(null);
  }, [initialSlug, open]);

  const invalidSlug = Boolean(initialSlug) && !getProductBySlug(initialSlug);
  const results = searchProducts(query);
  const selectedProduct = selectedSlug ? getProductBySlug(selectedSlug) : null;
  const selectedVariant =
    selectedProduct && selectedCode
      ? selectedProduct.variants.find((v) => v.code === selectedCode)
      : null;
  const orderable = variantIsOrderable(selectedVariant);

  // Purchase command "buy [CODE]"; null when no valid available variant selected.
  const orderUrl = orderable ? buildBuyWhatsAppUrl(selectedVariant.code) : null;

  const handlePlatform = (slug) => {
    onSelectSlug(slug);
    // Bring package section into view on small screens.
    requestAnimationFrame(() => {
      variantsRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  };

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in" />
        <Dialog.Content
          data-testid="catalog-dialog"
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="fixed left-1/2 top-1/2 z-50 flex h-[100dvh] w-full max-w-3xl -translate-x-1/2 -translate-y-1/2 flex-col border border-nm-border bg-nm-bg2 shadow-2xl outline-none sm:h-[88vh] sm:rounded-2xl"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-3 border-b border-nm-border px-4 py-4 sm:px-6">
            <div>
              <Dialog.Title className="text-lg font-extrabold text-nm-text sm:text-xl">
                Katalog Produk Digital
              </Dialog.Title>
              <Dialog.Description className="mt-0.5 text-xs text-nm-muted sm:text-sm">
                Pilih platform, tentukan paket, lalu pesan lewat WhatsApp.
              </Dialog.Description>
            </div>
            <Dialog.Close
              data-testid="catalog-close"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-nm-muted transition-colors hover:bg-nm-surface2 hover:text-nm-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nm-red"
              aria-label="Tutup katalog"
            >
              <X className="h-5 w-5" />
            </Dialog.Close>
          </div>

          {invalidSlug ? (
            <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-nm-surface2">
                <PackageX className="h-8 w-8 text-nm-red" />
              </div>
              <h3 className="mt-4 text-lg font-bold text-nm-text">
                Produk tidak ditemukan
              </h3>
              <p className="mt-1 max-w-sm text-sm text-nm-muted">
                Tautan produk tidak valid. Lihat seluruh katalog untuk memilih
                platform.
              </p>
              <button
                type="button"
                data-testid="catalog-invalid-reset"
                onClick={() => onSelectSlug(null)}
                className="mt-5 inline-flex h-10 items-center justify-center rounded-lg bg-nm-red px-5 text-sm font-semibold text-white transition-colors hover:bg-nm-red-hover"
              >
                Lihat Semua Produk
              </button>
            </div>
          ) : (
            <>
              {/* Search */}
              <div className="border-b border-nm-border px-4 py-3 sm:px-6">
                <div className="relative">
                  <label htmlFor="catalog-search" className="sr-only">
                    Cari produk
                  </label>
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-nm-muted" />
                  <input
                    id="catalog-search"
                    data-testid="catalog-search"
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Cari platform, mis. Netflix, Dramabox..."
                    className="h-11 w-full rounded-lg border border-nm-border bg-nm-surface pl-10 pr-4 text-sm text-nm-text placeholder:text-nm-muted focus:border-nm-red focus:outline-none focus:ring-2 focus:ring-nm-red/30"
                  />
                </div>
                <p
                  data-testid="catalog-result-count"
                  className="mt-2 text-xs text-nm-muted"
                >
                  Menampilkan{" "}
                  <span className="font-semibold text-nm-text">
                    {results.length}
                  </span>{" "}
                  produk
                </p>
              </div>

              {/* Scrollable body */}
              <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-nm-muted">
                  Pilih platform
                </h3>
                {results.length === 0 ? (
                  <div
                    data-testid="catalog-empty"
                    className="rounded-xl border border-dashed border-nm-border py-12 text-center text-sm text-nm-muted"
                  >
                    Tidak ada produk yang cocok. Coba kata kunci lain.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2.5 xs:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                    {results.map((product) => {
                      const active = product.slug === selectedSlug;
                      return (
                        <button
                          key={product.slug}
                          type="button"
                          data-testid={`platform-tile-${product.slug}`}
                          aria-pressed={active}
                          onClick={() => handlePlatform(product.slug)}
                          className={`group flex flex-col items-center gap-2 rounded-xl border p-3 text-center transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nm-red ${
                            active
                              ? "border-nm-red bg-nm-surface glow-red"
                              : "border-nm-border bg-nm-surface hover:border-nm-red/50"
                          }`}
                        >
                          <ProductIcon
                            product={product}
                            className="h-12 w-12"
                          />
                          <span className="line-clamp-2 text-xs font-semibold leading-tight text-nm-text">
                            {product.name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Variant section */}
                <div ref={variantsRef} className="mt-6 scroll-mt-4">
                  {selectedProduct ? (
                    <>
                      <div className="mb-3 flex items-center gap-2.5">
                        <ProductIcon
                          product={selectedProduct}
                          className="h-9 w-9"
                        />
                        <h3 className="text-sm font-bold text-nm-text">
                          Paket {selectedProduct.name}
                        </h3>
                      </div>
                      <div
                        role="radiogroup"
                        aria-label={`Pilih paket ${selectedProduct.name}`}
                        className="grid grid-cols-1 gap-2 sm:grid-cols-2"
                      >
                        {selectedProduct.variants.map((variant) => {
                          const active = variant.code === selectedCode;
                          const canOrder = variantIsOrderable(variant);
                          return (
                            <button
                              key={variant.code}
                              type="button"
                              role="radio"
                              aria-checked={active}
                              disabled={!canOrder}
                              data-testid={`variant-option-${variant.code}`}
                              onClick={() => setSelectedCode(variant.code)}
                              className={`flex items-center justify-between gap-3 rounded-xl border p-3 text-left transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nm-red ${
                                active
                                  ? "border-nm-red bg-nm-surface glow-red"
                                  : "border-nm-border bg-nm-surface hover:border-nm-red/50"
                              } ${!canOrder ? "opacity-60" : ""}`}
                            >
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-nm-text">
                                  {variant.durationLabel}
                                </p>
                                <p className="mt-0.5 text-[11px] text-nm-muted">
                                  {variant.code}
                                </p>
                                <div className="mt-1.5 flex items-center gap-2">
                                  <PriceDisplay price={variant.price} size="sm" />
                                  <AvailabilityBadge available={variant.available} />
                                </div>
                              </div>
                              <span
                                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                                  active
                                    ? "border-nm-red bg-nm-red text-white"
                                    : "border-nm-border"
                                }`}
                              >
                                {active && <Check className="h-3 w-3" strokeWidth={3} />}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <div className="rounded-xl border border-nm-border bg-nm-surface/50 py-8 text-center text-sm text-nm-muted">
                      Pilih platform di atas untuk melihat paket dan harganya.
                    </div>
                  )}
                </div>
              </div>

              {/* Sticky footer: summary + order */}
              <div className="border-t border-nm-border bg-nm-bg2 px-4 py-3 sm:px-6">
                <p
                  data-testid="catalog-summary"
                  className="mb-2 text-sm text-nm-muted"
                >
                  {selectedVariant ? (
                    <>
                      Paket terpilih:{" "}
                      <span className="font-semibold text-nm-text">
                        {selectedVariant.code}
                      </span>{" "}
                      — <span className="font-semibold text-nm-green">
                        {formatIDR(selectedVariant.price)}
                      </span>
                    </>
                  ) : (
                    <>Pilih paket untuk melanjutkan pesanan.</>
                  )}
                </p>
                {orderUrl ? (
                  <a
                    data-testid="whatsapp-order-button"
                    href={orderUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-wa font-semibold text-white shadow-lg shadow-wa/20 transition-all duration-200 hover:bg-wa-hover active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wa focus-visible:ring-offset-2 focus-visible:ring-offset-nm-bg2"
                  >
                    <MessageCircle className="h-4.5 w-4.5" />
                    Beli via WhatsApp
                  </a>
                ) : (
                  <button
                    type="button"
                    data-testid="whatsapp-order-button"
                    disabled
                    aria-disabled="true"
                    className="inline-flex h-12 w-full cursor-not-allowed items-center justify-center gap-2 rounded-lg bg-nm-surface2 font-semibold text-nm-muted"
                  >
                    <MessageCircle className="h-4.5 w-4.5" />
                    Pilih Paket untuk Membeli
                  </button>
                )}
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
